<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { isQrActivityReport, type QrActivityReport } from '../qr-activity';
const report = ref<QrActivityReport | null>(null);
const totals = computed(() =>
  report.value?.trackingSince
    ? report.value.totals
    : { opened: 0, started: 0, paid: 0, pending: 0, failed: 0, abandoned: 0, blocked: 0 },
);
const metrics = [
  { key: 'opened', label: 'Ouvertures QR' },
  { key: 'started', label: 'Achats commencés' },
  { key: 'paid', label: 'Achats payés' },
  { key: 'pending', label: 'En attente' },
  { key: 'failed', label: 'Paiements en échec' },
  { key: 'abandoned', label: 'Abandons' },
  { key: 'blocked', label: 'Blocages' },
] as const;
const lastOpening = computed(() =>
  report.value?.trackingSince ? report.value.recentOpenings[0]?.at : null,
);
const loading = ref(false);
const error = ref('');
const date = (value: string) =>
  new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
async function load() {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  report.value = null;
  try {
    const response = await globalThis.fetch('/admin/api/qr-activity', {
      credentials: 'include',
    });
    if (!response.ok)
      throw new Error(
        'Le suivi QR est indisponible. Réessayez après reconnexion si nécessaire.',
      );
    const data = await response.json();
    if (!isQrActivityReport(data)) throw new Error('Réponse du suivi QR invalide.');
    report.value = data;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Suivi indisponible.';
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<template>
  <section class="qr-activity" aria-label="Visites QR et paiements">
    <div class="qr-heading">
      <div>
        <h3>QR et achats</h3>
        <span class="qr-period">{{ report?.truncated ? 'Période réduite' : '30 jours' }}</span>
      </div>
      <button type="button" :disabled="loading" @click="load">Actualiser</button>
    </div>
    <p v-if="loading" role="status">Chargement…</p>
    <p v-else-if="error" role="alert">{{ error }}</p>
    <dl v-else-if="report" class="qr-counts">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        :class="{
          'qr-success': metric.key === 'paid',
          'qr-pending': metric.key === 'pending' && totals.pending > 0,
          'qr-alert': ['failed', 'blocked'].includes(metric.key) && totals[metric.key] > 0,
        }"
      >
        <dt>{{ metric.label }}</dt>
        <dd>{{ totals[metric.key] }}</dd>
      </div>
      <div class="qr-last-opening">
        <dt>Dernière ouverture</dt>
        <dd>{{ lastOpening ? date(lastOpening) : '—' }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped>
.qr-activity {
  margin-block: 1rem;
  padding: 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 18px;
  background: #fff;
  color: #142337;
  overflow-wrap: anywhere;
}
.qr-heading {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
h3 {
  font-size: 1.3rem;
  margin: 0.3rem 0;
}
button {
  min-height: 44px;
  padding: 0.65rem 1rem;
  border: 0;
  border-radius: 10px;
  background: #0c2946;
  color: white;
  font-weight: 700;
}
button:disabled {
  opacity: 0.6;
}
p {
  line-height: 1.5;
}
.qr-counts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}
.qr-counts div {
  padding: 0.8rem;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #f1f5f9;
}
dt {
  font-size: 0.9rem;
}
dd {
  font-size: 1.7rem;
  font-weight: 800;
  margin: 0.4rem 0 0;
}
.qr-counts .qr-success {
  background: #eaf6ee;
  color: #14532d;
}
.qr-counts .qr-pending {
  background: #fff4e5;
  color: #7c3905;
}
.qr-counts .qr-alert {
  background: #fff1f2;
  color: #991b1b;
}
.qr-period {
  color: #475569;
  font-size: 0.85rem;
}
.qr-last-opening dd {
  font-size: 1.05rem;
  line-height: 1.5;
}
@media (min-width: 700px) {
  .qr-counts {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
